/**
 * shapeTreeSplice.ts — replace a slide's shapes with the redesigned shape tree.
 *
 * The endpoint returns `shapeTreeXml` as shape elements ONLY: the backend's
 * `extract_original_shape_tree` drops the `<p:nvGrpSpPr>` and `<p:grpSpPr>`
 * header that every `<p:spTree>` must open with. Dropping that header from the
 * slide would produce an invalid package, so the splice keeps the slide's own
 * header and replaces only the shapes that follow it.
 *
 * Pure string/XML work, deliberately kept free of Office.js and fflate so it is
 * directly unit-testable.
 */

/** Thrown when a slide's XML doesn't have the structure the splice requires. */
export class ShapeTreeSpliceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShapeTreeSpliceError";
  }
}

const SP_TREE_RE = /(<p:spTree(?:\s[^>]*)?>)([\s\S]*?)(<\/p:spTree>)/;

/**
 * The spTree's own `<p:grpSpPr>`. Matched from the START of the spTree body, so
 * the first hit is always the tree's own header — a nested `<p:grpSp>` carries
 * its own `grpSpPr`, but it can only appear after the header, never before it.
 */
const GRP_SP_PR_RE = /<p:grpSpPr(?:\s[^>]*)?(?:\/>|>[\s\S]*?<\/p:grpSpPr>)/;
const NV_GRP_SP_PR_RE = /<p:nvGrpSpPr(?:\s[^>]*)?(?:\/>|>[\s\S]*?<\/p:nvGrpSpPr>)/;

/**
 * True when the redesigned tree actually carries drawable content.
 *
 * The backend substitutes the slide's pre-redesign shapes when its own output
 * fails validation, but that fallback yields an empty string if the original
 * slide had no extractable shapes. Splicing that would silently blank the
 * slide, so an empty tree is treated as "nothing to apply".
 */
export function hasRenderableShapes(shapeTreeXml: string | null | undefined): boolean {
  if (!shapeTreeXml) return false;
  const trimmed = shapeTreeXml.trim();
  if (trimmed.length === 0) return false;
  // Mirrors the backend's own validity floor: a tree with no shape or picture
  // element cannot render anything.
  return trimmed.includes("<p:sp") || trimmed.includes("<p:pic");
}

/* ── Normalisation ─────────────────────────────────────────────── */

/** Marks a group as already-checked so the innermost scan stops re-matching it. */
const KEPT_GROUP = "p:grpSpKeep";

/**
 * A group shape is worth keeping only if something inside it draws. A already-kept
 * nested group counts, because it has already cleared this same bar.
 *
 * The trailing `[\s/>]` is load-bearing twice over: it stops `<p:sp` from matching
 * `<p:spPr`, and it must include `/` so a self-closing `<p:pic/>` still counts.
 * Built with `new RegExp` so KEPT_GROUP is interpolated rather than matched as
 * literal text.
 */
const DRAWABLE_CHILD_RE = new RegExp(
  String.raw`<p:(?:sp|pic|graphicFrame|cxnSp)[\s/>]|<${KEPT_GROUP}[\s/>]`,
);

/**
 * Matches a group shape containing no further group-shape opening tag — i.e. the
 * innermost one, which is the only level that can be judged without recursion.
 */
const INNERMOST_GROUP_RE = /<p:grpSp(?:\s[^>]*)?>((?:(?!<p:grpSp[\s>])[\s\S])*?)<\/p:grpSp>/;

/**
 * Make a redesigned shape tree safe to splice into a slide.
 *
 * Two corrections, both driven by what the live model actually emits:
 *   1. {@link normaliseConnectors} — connectors carrying shape geometry become shapes.
 *   2. Empty group shapes are dropped (below).
 *
 * Drop group shapes that contain nothing drawable.
 *
 * Observed against the live endpoint: the model frequently opens its output by
 * echoing the shape tree's own header wrapped in a `<p:grpSp>` —
 * `<p:grpSp><p:nvGrpSpPr><p:cNvPr id="1" .../>…</p:nvGrpSpPr><p:grpSpPr/></p:grpSp>`
 * — an empty group carrying `id="1"`. Splicing that in alongside the slide's real
 * header produces a duplicate shape id (OOXML requires them to be unique within
 * a slide) plus a content-free group. The backend does not strip it: its own
 * header filter only applies to the pre-redesign fallback path, not to the
 * model's raw output, and the group parses fine so validation passes.
 *
 * Empty groups are removed innermost-first and the scan repeats, so a group that
 * becomes empty once its children are dropped is removed too.
 */
/**
 * Preset geometries a `<p:cxnSp>` is allowed to carry. Everything else in
 * `ST_ShapeType` is a shape geometry and belongs on a `<p:sp>`.
 */
const CONNECTOR_GEOMETRIES: ReadonlySet<string> = new Set([
  "line",
  "lineInv",
  "straightConnector1",
  "bentConnector2",
  "bentConnector3",
  "bentConnector4",
  "bentConnector5",
  "curvedConnector2",
  "curvedConnector3",
  "curvedConnector4",
  "curvedConnector5",
]);

const CONNECTOR_BLOCK_RE = /<p:cxnSp(?:\s[^>]*)?>[\s\S]*?<\/p:cxnSp>/g;
const PRESET_GEOMETRY_RE = /<a:prstGeom\b[^>]*\bprst="([^"]+)"/;

/** Rewrite one connector as an equivalent autoshape, dropping connector-only parts. */
function connectorToShape(block: string): string {
  return block
    .replace(/^<p:cxnSp(?:\s[^>]*)?>/, "<p:sp>")
    .replace(/<\/p:cxnSp>$/, "</p:sp>")
    .replace(/<p:nvCxnSpPr(?:\s[^>]*)?>/, "<p:nvSpPr>")
    .replace(/<\/p:nvCxnSpPr>/, "</p:nvSpPr>")
    // cNvCxnSpPr can hold <a:stCxn>/<a:endCxn>/<a:cxnSpLocks>, none of which are
    // valid on a shape, so the whole element is replaced rather than renamed.
    .replace(/<p:cNvCxnSpPr\s*\/>|<p:cNvCxnSpPr(?:\s[^>]*)?>[\s\S]*?<\/p:cNvCxnSpPr>/, "<p:cNvSpPr/>");
}

/**
 * Convert `<p:cxnSp>` elements that carry a non-connector geometry into `<p:sp>`.
 *
 * Observed against the live endpoint: the model emits decorative dividers as
 * connectors while giving them shape geometry, e.g.
 * `<p:cxnSp>…<a:prstGeom prst="chevron"/>…</p:cxnSp>`. PowerPoint refuses to open
 * a package containing one — "The file or directory is corrupted and unreadable"
 * — which surfaces through Office.js as `InvalidArgument` from
 * `insertSlidesFromBase64`. Neither the backend nor the desktop app catches it
 * first: the XML is well-formed, and the backend only checks well-formedness.
 *
 * Converting rather than dropping keeps the intended visual — verified against
 * PowerPoint, which accepts the converted package with every shape intact.
 * Genuine connectors (and connectors with no explicit geometry) are untouched.
 */
export function normaliseConnectors(shapeTreeXml: string): string {
  return shapeTreeXml.replace(CONNECTOR_BLOCK_RE, (block) => {
    const preset = PRESET_GEOMETRY_RE.exec(block)?.[1];
    if (!preset || CONNECTOR_GEOMETRIES.has(preset)) return block;
    return connectorToShape(block);
  });
}

export function normaliseShapeTree(shapeTreeXml: string): string {
  let working = normaliseConnectors(shapeTreeXml);

  for (;;) {
    const match = INNERMOST_GROUP_RE.exec(working);
    if (!match) break;

    const [block, body = ""] = match;
    const before = working.slice(0, match.index);
    const after = working.slice(match.index + block.length);

    working = DRAWABLE_CHILD_RE.test(body)
      ? // Keep it, but rename so the next scan moves past it instead of looping.
        before +
        block.replace(/^<p:grpSp/, `<${KEPT_GROUP}`).replace(/<\/p:grpSp>$/, `</${KEPT_GROUP}>`) +
        after
      : before + after;
  }

  return working.split(KEPT_GROUP).join("p:grpSp");
}

/**
 * Return `slideXml` with the contents of its `<p:spTree>` replaced by
 * `shapeTreeXml`, preserving the tree's `nvGrpSpPr`/`grpSpPr` header.
 *
 * @throws {ShapeTreeSpliceError} when the slide has no shape tree, the tree has
 * no recognisable header, or the replacement tree is empty.
 */
export function spliceShapeTree(slideXml: string, shapeTreeXml: string): string {
  if (!slideXml) throw new ShapeTreeSpliceError("The slide XML is empty.");

  // Normalise BEFORE the emptiness check: a tree whose only content was an empty
  // group has nothing to apply, and must not be spliced in as a blank slide.
  const normalised = normaliseShapeTree(shapeTreeXml ?? "").trim();
  if (!hasRenderableShapes(normalised)) {
    throw new ShapeTreeSpliceError("The redesigned slide contained no shapes to apply.");
  }

  const match = SP_TREE_RE.exec(slideXml);
  if (!match) throw new ShapeTreeSpliceError("The slide has no shape tree to replace.");

  const [, openTag = "", body = "", closeTag = ""] = match;

  const header = extractShapeTreeHeader(body);
  const rebuilt = `${openTag}${header}${normalised}${closeTag}`;
  return slideXml.slice(0, match.index) + rebuilt + slideXml.slice(match.index + match[0].length);
}

/**
 * Everything up to and including the spTree's `<p:grpSpPr>` — the required
 * header that must survive the replacement.
 */
function extractShapeTreeHeader(shapeTreeBody: string): string {
  const grpSpPr = GRP_SP_PR_RE.exec(shapeTreeBody);
  if (grpSpPr) return shapeTreeBody.slice(0, grpSpPr.index + grpSpPr[0].length);

  // A tree with visual properties omitted still declares its non-visual ones.
  const nvGrpSpPr = NV_GRP_SP_PR_RE.exec(shapeTreeBody);
  if (nvGrpSpPr) return shapeTreeBody.slice(0, nvGrpSpPr.index + nvGrpSpPr[0].length);

  throw new ShapeTreeSpliceError("The slide's shape tree is missing its group properties.");
}
